import { getStatus, OperationOutcomeError } from '@medplum/core';
import type { MedplumClient } from '@medplum/core';
import type { Appointment, Encounter } from '@medplum/fhirtypes';

/** A version conflict re-reads and re-applies the change this many times in total. */
const UPDATE_ATTEMPTS = 3;

function isPreconditionFailed(err: unknown): boolean {
  return err instanceof OperationOutcomeError && getStatus(err.outcome) === 412;
}

/**
 * Applies `change` to a fresh read of the resource and saves it with If-Match, so a UI write cannot
 * clobber fields other writers (e.g. the chime-telehealth bot's session state, or a client's session
 * location on the Encounter) saved after this page loaded its copy. Retries on 412. Returning the
 * fresh resource unchanged skips the write.
 */
export async function updateResourceFresh<T extends Appointment | Encounter>(
  medplum: Pick<MedplumClient, 'readResource' | 'updateResource'>,
  resourceType: T['resourceType'],
  id: string,
  change: (fresh: T) => T,
): Promise<T> {
  for (let attempt = 1; ; attempt++) {
    const fresh = (await medplum.readResource(resourceType, id, { cache: 'no-cache' })) as T;
    const next = change(fresh);
    if (next === fresh) return fresh;
    const versionId = fresh.meta?.versionId;
    try {
      return await medplum.updateResource(
        next,
        versionId ? { headers: { 'If-Match': `W/"${versionId}"` } } : undefined,
      );
    } catch (err) {
      if (!isPreconditionFailed(err) || attempt >= UPDATE_ATTEMPTS) throw err;
    }
  }
}

/** {@link updateResourceFresh} for an Appointment. */
export function updateAppointmentFresh(
  medplum: Pick<MedplumClient, 'readResource' | 'updateResource'>,
  appointmentId: string,
  change: (fresh: Appointment) => Appointment,
): Promise<Appointment> {
  return updateResourceFresh<Appointment>(medplum, 'Appointment', appointmentId, change);
}
